export class scribbleGenerator {
  constructor(container) {
    if (typeof container === "string") {
      container = document.querySelector(container);
    }
    if (!container) {
      throw new Error("Container element not found");
    }
    this.container = container;

    this.isMobile = window.innerWidth < 768;

    // デフォルト値を新しい仕様に合わせて更新 (1-100の割合)
    const defaults = {
      lineWidth: 6,
      complexity: 7,      // 複雑さ (1-10)
      maxLength: 50,      // 相対的な最大長 (1-100)
      strokeColor: "#000000",
      bounds: 0,          // 中央への重力 (0-10, 0=現在の動作, 10=最大の重力)
      speed: 50,          // 相対的なスピード (1-100)
      continuous: false
    };

    this.opts = { ...defaults };
    this.applyDataAttributes();

    this.canvas = container.querySelector("canvas") || document.createElement("canvas");
    if (!this.canvas.parentNode) {
      container.appendChild(this.canvas);
    }
    this.ctx = this.canvas.getContext("2d");

    this.pathSegments = [];
    this.currentX = 0;
    this.currentY = 0;
    this.segmentProgress = 0;
    this.tailProgress = 0;
    // ペン先を弧長(ピクセル)基準で進めるための状態
    // tは曲線ごとに進む速さが違うため、t基準だと継ぎ目で速度が跳ねてカクつく
    this.headDistance = 0;
    this.tailDistance = 0;
    this.avgSegmentLength = 0;
    this.lastFrameTime = null;
    this.animationId = null;
    this.isPaused = false;

    // 計算されたオプション値を保持するプロパティ
    this.calculatedMaxLength = 0;

    this.init();
  }

  applyDataAttributes() {
    const prefix = "data-scribble-";

    Object.keys(this.opts).forEach(key => {
      const desktopAttr = this.container.getAttribute(`${prefix}${this.camelToKebab(key)}`);
      const mobileAttr = this.container.getAttribute(`${prefix}${this.camelToKebab(key)}-mobile`);

      if (this.isMobile) {
        if (mobileAttr !== null) {
          this.opts[key] = this.parseValue(mobileAttr);
        } else if (desktopAttr !== null) {
          this.opts[key] = this.parseValue(desktopAttr);
        }
      } else {
        if (desktopAttr !== null) {
          this.opts[key] = this.parseValue(desktopAttr);
        }
      }
    });
  }

  camelToKebab(str) {
    return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
  }

  parseValue(value) {
    if (value === "true") return true;
    if (value === "false") return false;

    if (!isNaN(parseFloat(value)) && isFinite(value)) {
      return parseFloat(value);
    }
    return value;
  }

  adjustCanvasSize() {
    const rect = this.container.getBoundingClientRect();
    const width = Math.floor(rect.width);
    const height = Math.floor(rect.height);

    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = width * dpr;
    this.canvas.height = height * dpr;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    this.canvas.style.width = width + "px";
    this.canvas.style.height = height + "px";

    return { width, height };
  }
  
  /**
   * [NEW] 相対的なオプション値から、アニメーションで使う具体的な値を計算します。
   * このメソッドは init() と resize() で呼び出されます。
   */
  calculateDynamicOptions() {
    const { width, height } = this.adjustCanvasSize();
    
    // maxLength: 1-100の割合から、実際のセグメント数を計算
    // コンテナのサイズに比例させることで、どの大きさの要素でも見た目の密度が近くなる
    const baseSegments = (width + height) / 12; // 係数は見た目で調整
    const maxLengthRatio = this.opts.maxLength / 100;
    
    // 最低でも2セグメント (moveTo + 1つのカーブ) は必要
    this.calculatedMaxLength = Math.max(2, Math.floor(baseSegments * maxLengthRatio));
  }


  init() {
    this.calculateDynamicOptions();
    
    const { width, height } = { width: this.canvas.width / (window.devicePixelRatio||1), height: this.canvas.height / (window.devicePixelRatio||1) };

    // より広い範囲から開始位置を選択
    const padding = 40;
    this.currentX = padding + Math.random() * (width - padding * 2);
    this.currentY = padding + Math.random() * (height - padding * 2);

    this.pathSegments.push({
      type: "moveTo",
      x: this.currentX,
      y: this.currentY
    });

    this.animate(performance.now());
  }

  generateNextSegment() {
    const { width, height } = { width: this.canvas.width / (window.devicePixelRatio||1), height: this.canvas.height / (window.devicePixelRatio||1) };
    const random = (min, max) => Math.random() * (max - min) + min;

    // complexity: 1-10の値を正規化して使用
    const complexity = Math.max(1, Math.min(10, this.opts.complexity));
    const normalizedComplexity = (complexity - 1) / 9;
    const baseScale = Math.min(width / 200, height / 150);

    const controlRange = baseScale * (20 + normalizedComplexity * 60);
    const moveRange = baseScale * (30 + normalizedComplexity * 70);

    const aspectRatio = width / height;
    const xMult = aspectRatio > 1 ? Math.sqrt(aspectRatio) : 1;
    const yMult = aspectRatio < 1 ? Math.sqrt(1 / aspectRatio) : 1;

    // bounds: 0-10の値を正規化して重力の強さを決定
    const bounds = Math.max(0, Math.min(10, this.opts.bounds));
    const normalizedBounds = bounds / 10;
    const centerX = width / 2;
    const centerY = height / 2;

    let cp1x, cp1y, cp2x, cp2y, nextX, nextY;

    // 前セグメントの出口接線を取得(一筆書きの滑らかな接続のため)
    // pathSegments の最終要素から導出するので、重力・エッジ補正・クランプ後の
    // 最終値が常に反映される(restart/連続モードのspliceにも影響されない)
    const prevSeg = this.pathSegments[this.pathSegments.length - 1];
    let tanX = 0, tanY = 0;
    let hasPrevTangent = false;
    if (prevSeg && prevSeg.type === "bezierCurveTo") {
      const dx = prevSeg.x - prevSeg.cp2x;
      const dy = prevSeg.y - prevSeg.cp2y;
      const len = Math.sqrt(dx * dx + dy * dy);
      if (len > 0.001) {  // cp2が終点と一致する退化ケース(0除算)を除外
        tanX = dx / len;
        tanY = dy / len;
        hasPrevTangent = true;
      }
    }
    const prevAngle = hasPrevTangent ? Math.atan2(tanY, tanX) : null;

    if (complexity <= 2) {
      // 前の進行方向を弱く引き継ぐ(緩やかな蛇行)
      const maxTurn = Math.PI * 0.25; // ±45°
      const angle = hasPrevTangent
        ? prevAngle + random(-maxTurn, maxTurn)
        : random(0, Math.PI * 2);
      const distance = moveRange;

      nextX = this.currentX + Math.cos(angle) * distance * xMult;
      nextY = this.currentY + Math.sin(angle) * distance * yMult;

      const chordX = nextX - this.currentX;
      const chordY = nextY - this.currentY;
      const chordLen = Math.sqrt(chordX * chordX + chordY * chordY);

      // cp1 は入口接線方向に置く(wobbleを加えると角が出るので加えない)
      if (hasPrevTangent) {
        cp1x = this.currentX + tanX * chordLen * 0.33;
        cp1y = this.currentY + tanY * chordLen * 0.33;
      } else {
        cp1x = this.currentX + chordX * 0.33;
        cp1y = this.currentY + chordY * 0.33;
      }

      // cp2 のみ揺らぎを持たせる
      const wobble = controlRange * 0.1;
      cp2x = this.currentX + chordX * 0.67 + random(-wobble, wobble);
      cp2y = this.currentY + chordY * 0.67 + random(-wobble, wobble);
    } else {
      // 新しい進行方向: 前の接線方向 ± 制限付きランダム回転(complexityが高いほど大きく曲がる)
      // 広めの回転範囲で従来のランダムな彷徨いを維持する(印象を変えないため)
      // complexity 1→±45°, 5→±90°, 10→±135°
      const maxTurn = Math.PI * (0.25 + normalizedComplexity * 0.5);
      const angle = hasPrevTangent
        ? prevAngle + random(-maxTurn, maxTurn)
        : random(0, Math.PI * 2);

      const distance = moveRange * random(0.5, 1.0);
      nextX = this.currentX + Math.cos(angle) * distance * xMult;
      nextY = this.currentY + Math.sin(angle) * distance * yMult;

      // cp1: 前セグメントのcp2を接続点で反射して接線連続(G1)にする
      // 長さは反射長 × k(0.4-0.8) を controlRange 内にクランプ(暴走防止)
      if (hasPrevTangent) {
        const prevLen = Math.sqrt(
          (prevSeg.x - prevSeg.cp2x) * (prevSeg.x - prevSeg.cp2x) +
          (prevSeg.y - prevSeg.cp2y) * (prevSeg.y - prevSeg.cp2y)
        );
        const k = random(0.4, 0.8);
        const cp1Len = Math.max(controlRange * 0.2, Math.min(controlRange, prevLen * k));
        cp1x = this.currentX + tanX * cp1Len;
        cp1y = this.currentY + tanY * cp1Len;
      } else {
        // 最初のカーブ: 進行方向に沿ってcp1を置く
        const cp1Len = controlRange * random(0.3, 0.6);
        cp1x = this.currentX + Math.cos(angle) * cp1Len * xMult;
        cp1y = this.currentY + Math.sin(angle) * cp1Len * yMult;
      }

      // cp2: 終点の手前(進行方向の逆側)に置き、垂直方向のジッターで筆致の揺らぎを出す
      const cp2Back = controlRange * random(0.3, 0.7);
      const jitter = controlRange * 0.35 * normalizedComplexity;
      const perpX = -Math.sin(angle);
      const perpY = Math.cos(angle);
      const j = random(-jitter, jitter);
      cp2x = nextX - Math.cos(angle) * cp2Back * xMult + perpX * j;
      cp2y = nextY - Math.sin(angle) * cp2Back * yMult + perpY * j;
    }

    // boundsに基づいて中央への重力を適用
    if (normalizedBounds > 0) {
      const distanceFromCenterX = this.currentX - centerX;
      const distanceFromCenterY = this.currentY - centerY;
      const distanceFromCenter = Math.sqrt(distanceFromCenterX * distanceFromCenterX + distanceFromCenterY * distanceFromCenterY);
      const maxDistance = Math.sqrt(centerX * centerX + centerY * centerY);
      const normalizedDistance = distanceFromCenter / maxDistance;
      
      // 中心から離れるほど強い重力をかける
      const gravityStrength = normalizedBounds * normalizedDistance * 0.5;
      
      // 次の位置を中央に向けて調整
      nextX -= distanceFromCenterX * gravityStrength;
      nextY -= distanceFromCenterY * gravityStrength;
      
      // コントロールポイントも中央に向けて調整（より自然な曲線のため）
      // 注意: cp1 は接線連続(G1)のため反射で決めているので動かさない
      const cpGravity = gravityStrength * 0.6;
      cp2x -= (cp2x - centerX) * cpGravity * 0.3;
      cp2y -= (cp2y - centerY) * cpGravity * 0.3;
    }

    // 端に近い場合、内側に向かう力を自然に加える
    const edgeZone = 40; // エッジゾーンの幅
    const edgeForce = 0.7; // エッジでの反発力
    
    // X軸の調整
    if (nextX < edgeZone) {
      const distance = edgeZone - nextX;
      const force = (distance / edgeZone) * edgeForce;
      nextX += moveRange * force * 0.5;
      // コントロールポイントも調整
      cp2x += moveRange * force * 0.3;
    } else if (nextX > width - edgeZone) {
      const distance = nextX - (width - edgeZone);
      const force = (distance / edgeZone) * edgeForce;
      nextX -= moveRange * force * 0.5;
      // コントロールポイントも調整
      cp2x -= moveRange * force * 0.3;
    }
    
    // Y軸の調整
    if (nextY < edgeZone) {
      const distance = edgeZone - nextY;
      const force = (distance / edgeZone) * edgeForce;
      nextY += moveRange * force * 0.5;
      // コントロールポイントも調整
      cp2y += moveRange * force * 0.3;
    } else if (nextY > height - edgeZone) {
      const distance = nextY - (height - edgeZone);
      const force = (distance / edgeZone) * edgeForce;
      nextY -= moveRange * force * 0.5;
      // コントロールポイントも調整
      cp2y -= moveRange * force * 0.3;
    }
    
    // 最終的に安全な範囲内に収める（ソフトな制限）
    const safeMargin = 10;
    nextX = Math.max(safeMargin, Math.min(width - safeMargin, nextX));
    nextY = Math.max(safeMargin, Math.min(height - safeMargin, nextY));

    return {
      type: "bezierCurveTo",
      cp1x, cp1y, cp2x, cp2y,
      x: nextX, y: nextY
    };
  }

  getCubicPoint(ax, ay, bx, by, cx, cy, dx, dy, t) {
    const mt = 1 - t;
    const mt2 = mt * mt;
    const t2 = t * t;
    return {
      x: ax * mt2 * mt + 3 * bx * mt2 * t + 3 * cx * mt * t2 + dx * t2 * t,
      y: ay * mt2 * mt + 3 * by * mt2 * t + 3 * cy * mt * t2 + dy * t2 * t
    };
  }

  // セグメントの弧長テーブルを作成する
  // ペン先を一定のピクセル速度で進めるため、距離→tの変換に使う
  buildArcLength(seg, startX, startY) {
    const samples = 24;
    const lut = [0];
    let total = 0;
    let px = startX, py = startY;
    for (let i = 1; i <= samples; i++) {
      const p = this.getCubicPoint(startX, startY, seg.cp1x, seg.cp1y, seg.cp2x, seg.cp2y, seg.x, seg.y, i / samples);
      total += Math.sqrt((p.x - px) * (p.x - px) + (p.y - py) * (p.y - py));
      lut.push(total);
      px = p.x;
      py = p.y;
    }
    seg.arcLUT = lut;
    seg.arcLength = Math.max(total, 0.5); // 退化セグメントでの無限ループを防ぐ
  }

  // 弧長(ピクセル)を曲線の媒介変数tに変換する
  tForDistance(seg, dist) {
    const lut = seg.arcLUT;
    if (!lut) return 0;
    const d = Math.max(0, Math.min(dist, seg.arcLength));
    let i = 1;
    while (i < lut.length && lut[i] < d) i++;
    if (i >= lut.length) return 1;
    const span = lut[i] - lut[i - 1];
    const frac = span > 0 ? (d - lut[i - 1]) / span : 0;
    return (i - 1 + frac) / (lut.length - 1);
  }

  // 新しいセグメントを生成して弧長情報とともに追加する
  pushSegment() {
    const last = this.pathSegments[this.pathSegments.length - 1];
    if (last) {
      this.currentX = last.x;
      this.currentY = last.y;
    }
    const seg = this.generateNextSegment();
    this.buildArcLength(seg, this.currentX, this.currentY);
    this.pathSegments.push(seg);
    // ペン速度の基準となる平均セグメント長(移動平均)
    this.avgSegmentLength = this.avgSegmentLength > 0
      ? this.avgSegmentLength * 0.9 + seg.arcLength * 0.1
      : seg.arcLength;
  }

  drawPath() {
    const { width, height } = { width: this.canvas.width / (window.devicePixelRatio||1), height: this.canvas.height / (window.devicePixelRatio||1) };

    this.ctx.clearRect(0, 0, width, height);
    this.ctx.lineWidth = Math.min(this.opts.lineWidth, 50);
    this.ctx.lineCap = "round";
    this.ctx.lineJoin = "round";
    this.ctx.strokeStyle = this.opts.strokeColor;

    this.ctx.beginPath();

    if (this.pathSegments.length < 2) {
      this.ctx.stroke();
      return;
    }

    const firstCurve = this.pathSegments[1];
    const startingPoint = this.tailProgress > 0 ?
      this.getCubicPoint(
        this.pathSegments[0].x, this.pathSegments[0].y,
        firstCurve.cp1x, firstCurve.cp1y,
        firstCurve.cp2x, firstCurve.cp2y,
        firstCurve.x, firstCurve.y,
        this.tailProgress
      ) :
      this.pathSegments[0];
    
    this.ctx.moveTo(startingPoint.x, startingPoint.y);

    for (let i = 1; i < this.pathSegments.length; i++) {
      const seg = this.pathSegments[i];
      const prevSeg = this.pathSegments[i - 1];
      const prevX = prevSeg.x;
      const prevY = prevSeg.y;

      const isFirstCurve = i === 1;
      const isLastCurve = i === this.pathSegments.length - 1;

      if (isFirstCurve && this.tailProgress > 0) {
        const steps = 30;
        const startT = this.tailProgress;
        for (let j = 1; j <= steps; j++) {
          const t = startT + (1 - startT) * (j / steps);
          const point = this.getCubicPoint(prevX, prevY, seg.cp1x, seg.cp1y, seg.cp2x, seg.cp2y, seg.x, seg.y, t);
          this.ctx.lineTo(point.x, point.y);
        }
      }
      else if (isLastCurve && this.segmentProgress < 1) {
        const steps = 30;
        const endT = this.segmentProgress;
        for (let j = 1; j <= steps; j++) {
          const t = endT * (j / steps);
          const point = this.getCubicPoint(prevX, prevY, seg.cp1x, seg.cp1y, seg.cp2x, seg.cp2y, seg.x, seg.y, t);
          this.ctx.lineTo(point.x, point.y);
        }
      }
      else {
        this.ctx.bezierCurveTo(seg.cp1x, seg.cp1y, seg.cp2x, seg.cp2y, seg.x, seg.y);
      }
    }

    this.ctx.stroke();
  }

  animate = (currentTime) => {
    if (this.isPaused) return;
    
    if (!this.lastFrameTime) {
      this.lastFrameTime = currentTime;
    }

    // タブ復帰などでdeltaTimeが跳ねたときにペン先が瞬間移動しないよう上限を設ける
    const deltaTime = Math.min(currentTime - this.lastFrameTime, 50);
    this.lastFrameTime = currentTime;

    // speed: 1-100の割合からアニメーションの増分を計算
    // 1-100は 0.2〜3.0x にマッピング(通常の描画スピード)。
    // 100超はドネーションの伸縮を素早くするための領域で、線形に加速し続ける(最大1000でクランプ)。
    const normalizedSpeed = (Math.max(1, Math.min(1000, this.opts.speed)) - 1) / 99; // 1-100で0-1、100超で1超
    const speedMultiplier = 0.2 + normalizedSpeed * 2.8; // 100で3.0x、1000で約28x

    // 最初のカーブをすぐに用意する
    if (this.pathSegments.length < 2) {
      this.pushSegment();
    }

    // ペン先の増分を弧長(ピクセル)基準で計算する
    // 従来のt基準(1セグメント/1単位時間)だとセグメントの長短で速度が跳ねてカクつくため、
    // 平均セグメント長 × speedMultiplier = 一定のピクセル速度で進める
    const pxIncrement = speedMultiplier * this.avgSegmentLength * (deltaTime / 1000);

    this.headDistance += pxIncrement;
    let lastSeg = this.pathSegments[this.pathSegments.length - 1];
    while (this.headDistance >= lastSeg.arcLength) {
      // maxLengthは計算済みの this.calculatedMaxLength を使用
      if (!this.opts.continuous && this.pathSegments.length >= this.calculatedMaxLength) {
        // ペン先が上限に到達: 終端でクランプして停止(setMaxLengthで再び伸ばせる)
        this.headDistance = lastSeg.arcLength;
        break;
      }
      this.headDistance -= lastSeg.arcLength;
      this.pushSegment();
      lastSeg = this.pathSegments[this.pathSegments.length - 1];
    }
    this.segmentProgress = this.tForDistance(lastSeg, this.headDistance);

    // 尾の追従:
    // - 連続モード: 常にペン先と同じ速度で追従(見た目の線の長さが一定になる)
    // - 非連続モード: setMaxLengthで上限が下がったときだけ、目標の長さまで縮める
    if (this.pathSegments.length > this.calculatedMaxLength) {
      this.tailDistance += pxIncrement;
      let firstCurve = this.pathSegments[1];
      while (firstCurve && this.tailDistance >= firstCurve.arcLength) {
        if (this.pathSegments.length <= 2) break;
        this.tailDistance -= firstCurve.arcLength;
        this.pathSegments.splice(0, 2);
        this.pathSegments.unshift({
          type: 'moveTo',
          x: firstCurve.x,
          y: firstCurve.y
        });
        firstCurve = this.pathSegments[1];
        // 非連続モード: 目標の長さまで縮んだらそれ以上食べない
        if (!this.opts.continuous && this.pathSegments.length <= this.calculatedMaxLength) break;
      }
      this.tailProgress = this.tForDistance(this.pathSegments[1], this.tailDistance);
    }

    this.drawPath();

    // 非連続モードで伸びも縮みも完了したら停止する(次のsetMaxLengthで再開)
    const headDone = !this.opts.continuous &&
      this.pathSegments.length >= this.calculatedMaxLength &&
      this.segmentProgress >= 1;
    const tailDone = this.pathSegments.length <= this.calculatedMaxLength;
    if (headDone && tailDone) {
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
      }
      this.animationId = null;
      return;
    }

    if (!this.isPaused) {
      this.animationId = requestAnimationFrame(this.animate);
    }
  }

  /**
   * 線の長さ(maxLength: 1-100)をアニメーション付きで変更する。
   * 現在より長ければペン先が伸び、短ければ尾側から縮む。
   * 描画が停止していれば再開する。
   */
  setMaxLength(value) {
    this.opts.maxLength = value;
    this.calculateDynamicOptions();
    if (!this.isPaused && !this.animationId) {
      this.lastFrameTime = null;
      this.animationId = requestAnimationFrame(this.animate);
    }
  }

  resize() {
    this.isMobile = window.innerWidth < 768;
    this.applyDataAttributes();
    // サイズ変更時にオプションを再計算
    this.calculateDynamicOptions();
    this.drawPath();
  }

  pause() {
    this.isPaused = true;
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }
  
  restart() {
    this.isPaused = false;
    // 現在の幅に応じたパラメータ(mobile/desktop)を読み直す
    this.isMobile = window.innerWidth < 768;
    this.applyDataAttributes();
    // アニメーションをリセット
    this.pathSegments = [];
    this.segmentProgress = 0;
    this.tailProgress = 0;
    this.headDistance = 0;
    this.tailDistance = 0;
    this.avgSegmentLength = 0;
    this.lastFrameTime = null;

    // 再初期化
    this.init();
  }
  
  destroy() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
    if (this.canvas && this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas);
    }
  }
}